import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ClipboardList,
  Database,
  Download,
  FileText,
  Link2,
  Paperclip,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent } from "@components/common/Card";
import { Button } from "@components/common/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/common/Dialog";
import { Input } from "@components/common/Input";
import { SearchableSelect } from "@components/common/SearchableSelect";
import { DocumentsPager } from "@components/Review/DocumentsPager";
import { PendingDocumentsTable } from "@components/Review/PendingDocumentsTable";
import { UnifiedUploadForm } from "@components/Upload/UnifiedUploadForm";
import { useGetErpConnections } from "@api/hooks/useErpConnection";
import {
  useExportDocuments,
  useFilteredDocuments,
  type DocumentFilters,
} from "@api/hooks/useWorkflowStatus";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { ERPConnectionDetailResponse } from "@schemas/api";

type DocumentTypeFilter = "" | "purchase_order" | "receipt" | "invoice";
type ExtractionStatusFilter =
  | ""
  | "pending_extraction"
  | "extracting"
  | "extracted"
  | "pending_review"
  | "approved"
  | "rejected";
type MatchStatusFilter =
  | ""
  | "unmatched"
  | "matching"
  | "matched"
  | "hold"
  | "split"
  | "reject";
type ReviewStage =
  | "extraction"
  | "approval_progress"
  | "ap_posting"
  | "payment";
type SortField =
  | "created_at"
  | "vendor_name"
  | "amount"
  | "confidence"
  | "erp_link";
type SortOrder = "asc" | "desc";
type ExportOptionKey =
  | "include_source_files"
  | "include_invoice_attachments"
  | "include_extraction_data"
  | "include_erp_linking_data"
  | "include_review_metadata";

const DEFAULT_SORT_ORDER: Record<SortField, SortOrder> = {
  created_at: "desc",
  amount: "desc",
  vendor_name: "asc",
  confidence: "asc",
  erp_link: "asc",
};

const erpConnectionLabel = (connection: ERPConnectionDetailResponse) =>
  connection.display_name ||
  connection.external_company_name ||
  `${connection.erp_type} ${connection.realm_id}`;

const exportOptionLabels: Array<{
  key: ExportOptionKey;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    key: "include_source_files",
    label: "Invoice/source documents",
    description: "Original PDFs and images for each exported document record.",
    icon: FileText,
  },
  {
    key: "include_invoice_attachments",
    label: "Invoice attachments",
    description: "Additional supporting files attached during invoice review.",
    icon: Paperclip,
  },
  {
    key: "include_extraction_data",
    label: "Extraction data",
    description:
      "Extracted fields, confidence scores, tax IDs, and line items.",
    icon: Database,
  },
  {
    key: "include_erp_linking_data",
    label: "ERP linking data",
    description:
      "PO match details, ERP item selections, dynamic ERP fields, and line links.",
    icon: Link2,
  },
  {
    key: "include_review_metadata",
    label: "Review metadata",
    description: "Document flags and approval-chain state for audit support.",
    icon: ClipboardList,
  },
];

export const DocumentRecords = ({
  documentType,
  approvalOnly = false,
  hideHeader = false,
}: {
  documentType?: Exclude<DocumentTypeFilter, "">;
  approvalOnly?: boolean;
  hideHeader?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [documentTypeFilter, setDocumentTypeFilter] =
    useState<DocumentTypeFilter>(() => {
      if (documentType) return documentType;
      const param =
        searchParams.get("document_type") ?? searchParams.get("type");
      return param === "purchase_order" ||
        param === "receipt" ||
        param === "invoice"
        ? param
        : "";
    });
  const [extractionStatusFilter, setExtractionStatusFilter] =
    useState<ExtractionStatusFilter>(() => {
      if (approvalOnly) return "pending_review";
      const param = searchParams.get("extraction_status");
      const validStatuses: ExtractionStatusFilter[] = [
        "pending_extraction",
        "extracting",
        "extracted",
        "pending_review",
        "approved",
        "rejected",
      ];
      return validStatuses.includes(param as ExtractionStatusFilter)
        ? (param as ExtractionStatusFilter)
        : "";
    });
  const [matchStatusFilter, setMatchStatusFilter] = useState<MatchStatusFilter>(
    () => {
      const param = searchParams.get("match_status");
      const validStatuses: MatchStatusFilter[] = [
        "",
        "unmatched",
        "matching",
        "matched",
        "hold",
        "split",
        "reject",
      ];
      return validStatuses.includes(param as MatchStatusFilter)
        ? (param as MatchStatusFilter)
        : "";
    },
  );
  const [erpConnectionFilter, setErpConnectionFilter] = useState(
    () => searchParams.get("erp_connection_id") ?? "",
  );
  const [searchTerm, setSearchTerm] = useState(
    () => searchParams.get("search") ?? "",
  );
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [createdFrom, setCreatedFrom] = useState(
    () => searchParams.get("created_from") ?? "",
  );
  const [createdTo, setCreatedTo] = useState(
    () => searchParams.get("created_to") ?? "",
  );
  const [amountMin, setAmountMin] = useState(
    () => searchParams.get("amount_min") ?? "",
  );
  const [amountMax, setAmountMax] = useState(
    () => searchParams.get("amount_max") ?? "",
  );
  const [hasIssues, setHasIssues] = useState(
    () => searchParams.get("has_issues") === "true",
  );
  const [sortBy, setSortBy] = useState<SortField>(() => {
    const param = searchParams.get("sort_by");
    return param === "vendor_name" ||
      param === "amount" ||
      param === "confidence" ||
      param === "erp_link"
      ? param
      : "created_at";
  });
  const [sortOrder, setSortOrder] = useState<SortOrder>(() =>
    searchParams.get("sort_order") === "asc" ? "asc" : "desc",
  );
  const [page, setPage] = useState(() => {
    const param = Number(searchParams.get("page"));
    return Number.isFinite(param) && param >= 1 ? param : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const param = Number(searchParams.get("page_size"));
    return [25, 50, 100].includes(param) ? param : 25;
  });
  const hasMountedRef = useRef(false);
  const [isUploadOpen, setIsUploadOpen] = useState(
    () => searchParams.get("upload") === "true",
  );
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState<
    Record<ExportOptionKey, boolean>
  >({
    include_source_files: true,
    include_invoice_attachments: true,
    include_extraction_data: true,
    include_erp_linking_data: true,
    include_review_metadata: true,
  });
  const { data: erpConnections = [] } = useGetErpConnections();
  const deepLinkedDocumentId = searchParams.get("document_id");
  const deepLinkedDocumentTypeParam = searchParams.get("document_type");
  const deepLinkedReviewStageParam = searchParams.get("review_stage");
  const deepLinkedDocumentType =
    deepLinkedDocumentTypeParam === "purchase_order" ||
    deepLinkedDocumentTypeParam === "receipt" ||
    deepLinkedDocumentTypeParam === "invoice"
      ? deepLinkedDocumentTypeParam
      : null;
  const deepLinkedReviewStage: ReviewStage =
    deepLinkedReviewStageParam === "approval_progress" ||
    deepLinkedReviewStageParam === "ap_posting" ||
    deepLinkedReviewStageParam === "payment"
      ? deepLinkedReviewStageParam
      : "extraction";

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setPage(1);
  }, [
    documentTypeFilter,
    extractionStatusFilter,
    matchStatusFilter,
    erpConnectionFilter,
    debouncedSearchTerm,
    createdFrom,
    createdTo,
    amountMin,
    amountMax,
    hasIssues,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value: string | null | undefined) => {
      if (value) {
        nextParams.set(key, value);
      } else {
        nextParams.delete(key);
      }
    };

    setOrDelete("document_type", documentTypeFilter);
    if (
      !approvalOnly &&
      (nextParams.get("type") === "invoice" ||
        nextParams.get("type") === "receipt" ||
        nextParams.get("type") === "purchase_order")
    ) {
      nextParams.set("type", "documents");
    }
    setOrDelete("extraction_status", extractionStatusFilter);
    setOrDelete("match_status", matchStatusFilter);
    setOrDelete("erp_connection_id", erpConnectionFilter);
    nextParams.delete("erp_linked");
    setOrDelete("search", debouncedSearchTerm);
    setOrDelete("created_from", createdFrom);
    setOrDelete("created_to", createdTo);
    setOrDelete("amount_min", amountMin);
    setOrDelete("amount_max", amountMax);
    setOrDelete("has_issues", hasIssues ? "true" : "");
    setOrDelete("sort_by", sortBy === "created_at" ? "" : sortBy);
    setOrDelete("sort_order", sortOrder === "desc" ? "" : sortOrder);
    setOrDelete("page", page > 1 ? String(page) : "");
    setOrDelete("page_size", pageSize !== 25 ? String(pageSize) : "");

    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentTypeFilter,
    extractionStatusFilter,
    matchStatusFilter,
    erpConnectionFilter,
    debouncedSearchTerm,
    createdFrom,
    createdTo,
    amountMin,
    amountMax,
    hasIssues,
    sortBy,
    sortOrder,
    page,
    pageSize,
  ]);

  // Build filters for API call
  const buildFilters = (): DocumentFilters => {
    const filters: DocumentFilters = {};

    if (documentTypeFilter) {
      filters.document_type = documentTypeFilter;
    }

    if (extractionStatusFilter) {
      filters.extraction_status = extractionStatusFilter;
    }

    if (matchStatusFilter) {
      filters.match_status = matchStatusFilter;
    }

    if (erpConnectionFilter) {
      filters.erp_connection_id = erpConnectionFilter;
    }

    if (debouncedSearchTerm) {
      filters.search = debouncedSearchTerm;
    }

    if (createdFrom) {
      filters.created_from = createdFrom;
    }

    if (createdTo) {
      filters.created_to = createdTo;
    }

    if (amountMin) {
      filters.amount_min = Number(amountMin);
    }

    if (amountMax) {
      filters.amount_max = Number(amountMax);
    }

    if (hasIssues) {
      filters.has_issues = true;
    }

    filters.sort_by = sortBy;
    filters.sort_order = sortOrder;
    filters.skip = (page - 1) * pageSize;
    filters.limit = pageSize;

    return filters;
  };

  const filteredQuery = useFilteredDocuments(buildFilters());
  const exportDocuments = useExportDocuments();
  const isLoading = filteredQuery.isLoading;
  const documents = (filteredQuery.data as any)?.documents || [];
  const documentTotal = filteredQuery.data?.total ?? documents.length;
  const hasExportSelection = Object.values(exportOptions).some(Boolean);

  const handleReviewComplete = () => {
    filteredQuery.refetch();
  };

  const handleInitialDocumentClose = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("document_id");
    nextParams.delete("open_review");
    nextParams.delete("review_stage");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const documentTypeOptions = [
    { value: "", label: "All Document Types" },
    { value: "purchase_order", label: "Purchase Orders" },
    { value: "receipt", label: "Receipts" },
    { value: "invoice", label: "Invoices" },
  ];

  const extractionStatusOptions = [
    { value: "", label: "All Extraction Statuses" },
    { value: "pending_extraction", label: "Pending Extraction" },
    { value: "extracting", label: "Extracting" },
    { value: "extracted", label: "Extracted" },
    { value: "pending_review", label: "Pending Review" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const matchStatusOptions = [
    { value: "", label: "All Match Statuses" },
    { value: "unmatched", label: "Unmatched" },
    { value: "matching", label: "Matching" },
    { value: "matched", label: "Matched" },
    { value: "hold", label: "On Hold" },
    { value: "split", label: "Split" },
    { value: "reject", label: "Rejected" },
  ];

  const handleClearFilters = () => {
    setDocumentTypeFilter(documentType ?? "");
    setExtractionStatusFilter(approvalOnly ? "pending_review" : "");
    setMatchStatusFilter("");
    setErpConnectionFilter("");
    setSearchTerm("");
    setCreatedFrom("");
    setCreatedTo("");
    setAmountMin("");
    setAmountMax("");
    setHasIssues(false);
    setSortBy("created_at");
    setSortOrder("desc");
    setPage(1);
  };

  const handleSortChange = (field: SortField) => {
    if (field === sortBy) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder(DEFAULT_SORT_ORDER[field]);
    }
  };

  const handleExportOptionToggle = (key: ExportOptionKey) => {
    setExportOptions((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleExport = () => {
    const exportFilters = buildFilters();
    exportDocuments.mutate(
      {
        document_type: exportFilters.document_type,
        status: exportFilters.extraction_status,
        match_status: exportFilters.match_status,
        vendor_name: exportFilters.search,
        erp_connection_id: exportFilters.erp_connection_id,
        correlation_id: exportFilters.correlation_id,
        ...exportOptions,
        limit: 2000,
      },
      {
        onSuccess: () => setIsExportOpen(false),
      },
    );
  };

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-content-primary">
              {documentType === "invoice"
                ? "Invoices"
                : documentType === "receipt"
                  ? "Receipts"
                  : documentType === "purchase_order"
                    ? "Purchase orders"
                    : "Documents"}
            </h2>
            <p className="text-sm text-content-secondary">
              {approvalOnly
                ? "Documents awaiting review. Existing approval permissions and chains apply."
                : "Browse records, supporting files, review history and accounting status."}
            </p>
          </div>
          {!approvalOnly && (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsExportOpen(true)}
                className="inline-flex items-center gap-2 whitespace-nowrap"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
              <Button
                onClick={() => setIsUploadOpen(true)}
                className="inline-flex items-center gap-2 whitespace-nowrap"
              >
                <Upload className="h-4 w-4" />
                Upload
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        className="max-w-2xl"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-content-primary">
              Upload Documents
            </DialogTitle>
            <DialogDescription>
              Add purchase orders, receipts, or invoices. The system will detect
              and classify each file automatically.
            </DialogDescription>
          </DialogHeader>
          <UnifiedUploadForm compact />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isExportOpen}
        onOpenChange={setIsExportOpen}
        className="max-w-2xl"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-content-primary">
              Export Documents
            </DialogTitle>
            <DialogDescription>
              Create a ZIP archive for the current document filters.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-border bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900 dark:text-gray-300">
              {documentTotal} matching document{documentTotal === 1 ? "" : "s"}{" "}
              will be included, up to 2,000 records.
            </div>

            <div className="space-y-3">
              {exportOptionLabels.map((option) => {
                const Icon = option.icon;
                return (
                  <label
                    key={option.key}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      checked={exportOptions[option.key]}
                      onChange={() => handleExportOptionToggle(option.key)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                        {option.label}
                      </span>
                      <span className="block text-sm text-gray-600 dark:text-gray-400">
                        {option.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => setIsExportOpen(false)}
                disabled={exportDocuments.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleExport}
                loading={exportDocuments.isPending}
                disabled={!hasExportSelection || documentTotal === 0}
                className="inline-flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download ZIP
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Document Type Filter */}
            <div>
              <label
                htmlFor="document-type-filter"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Document Type
              </label>
              <SearchableSelect
                id="document-type-filter"
                aria-label="Document Type"
                options={documentTypeOptions.map((option) => ({
                  id: option.value,
                  label: option.label,
                }))}
                value={documentTypeFilter}
                disabled={!!documentType}
                onChange={(value) =>
                  setDocumentTypeFilter(value as DocumentTypeFilter)
                }
                placeholder="Document type"
                clearable={false}
              />
            </div>

            {/* Extraction Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Extraction Status
              </label>
              <SearchableSelect
                options={extractionStatusOptions.map((option) => ({
                  id: option.value,
                  label: option.label,
                }))}
                value={extractionStatusFilter}
                disabled={approvalOnly}
                onChange={(value) =>
                  setExtractionStatusFilter(value as ExtractionStatusFilter)
                }
                placeholder="Extraction status"
                clearable={false}
              />
            </div>

            {/* Match Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Match Status
              </label>
              <SearchableSelect
                options={matchStatusOptions.map((option) => ({
                  id: option.value,
                  label: option.label,
                }))}
                value={matchStatusFilter}
                onChange={(value) =>
                  setMatchStatusFilter(value as MatchStatusFilter)
                }
                placeholder="Match status"
                clearable={false}
              />
            </div>

            {/* Unified Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search
              </label>
              <Input
                type="text"
                placeholder="Search vendor, document #, or line item..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                className="w-full"
              />
            </div>

            {/* ERP Link Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                ERP Link
              </label>
              <SearchableSelect
                options={[
                  { id: "", label: "All ERP Links" },
                  ...erpConnections.map((connection) => ({
                    id: connection.id,
                    label: erpConnectionLabel(connection),
                  })),
                ]}
                value={erpConnectionFilter}
                onChange={setErpConnectionFilter}
                placeholder="All ERP Links"
                clearable={false}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
            {/* Date range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Created From
              </label>
              <Input
                type="date"
                value={createdFrom}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCreatedFrom(e.target.value)
                }
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Created To
              </label>
              <Input
                type="date"
                value={createdTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCreatedTo(e.target.value)
                }
                className="w-full"
              />
            </div>

            {/* Amount range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Amount Min
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={amountMin}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAmountMin(e.target.value)
                }
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Amount Max
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={amountMax}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAmountMax(e.target.value)
                }
                className="w-full"
              />
            </div>

            {/* Has issues */}
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={hasIssues}
                  onChange={(e) => setHasIssues(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                Has issues
              </label>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <button
                onClick={handleClearFilters}
                className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {filteredQuery.error && (
        <p role="alert" className="text-red-700">
          Unable to load records. Please retry.
        </p>
      )}
      <Card className="p-0">
        <CardContent className="p-0">
          <PendingDocumentsTable
            documents={documents}
            isLoading={isLoading}
            onReviewComplete={handleReviewComplete}
            initialDocumentId={deepLinkedDocumentId}
            initialDocumentType={deepLinkedDocumentType}
            initialStage={deepLinkedReviewStage}
            onInitialDocumentClose={handleInitialDocumentClose}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        </CardContent>
      </Card>

      <DocumentsPager
        page={page}
        pageSize={pageSize}
        total={documentTotal}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
};
